/* eslint-disable react/no-array-index-key */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { IoIosArrowBack } from 'react-icons/io';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  AreaChart,
  Area,
} from 'recharts';

import { ExplainDate, Layout } from '~/components';
import { useAuth } from '~/hooks/auth';
import api from '~/services/api';

import { IComercial } from '../../types';

import * as S from '../styles';

const months = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Maio',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

const CommercialReports = () => {
  const { goBack } = useHistory();
  const {
    user: { professional },
  } = useAuth();

  const {
    state: { startDate, endDate },
  } = useLocation<any>();

  const [comertialReportsdata, setComertialReportsdata] = useState<IComercial>(
    {} as IComercial,
  );
  const [trainingCount, setTrainingCount] = useState<any>([]);
  const [studentsByAge, setStudentsByAge] = useState<any>([]);
  const [studentsByCountry, setStudentsByCountry] = useState<any>([]);
  const [studentsByCity, setStudentsByCity] = useState<any>([]);
  const [annualBilling, setAnnualBilling] = useState<any>([]);

  const getComercialReports = useCallback(async () => {
    try {
      const { data } = await api.get<IComercial>(
        `/reports/${professional.id}/financial-manangements`,
        {
          params: {
            startDate,
            endDate,
          },
        },
      );
      setComertialReportsdata(data);

      const countTrainingCurrentYearFormattedData =
        data.countTrainingCurrentYear.map((trainingCurrent) => {
          return {
            count: trainingCurrent.count,
            status: trainingCurrent.status ? 'Ativo' : 'Inativo',
          };
        });
      setTrainingCount(countTrainingCurrentYearFormattedData);

      const studentsByAgeFormattedData = data.studentsByAge.map((student) => {
        return {
          count: student.count,
          status: student.age,
        };
      });
      setStudentsByAge(studentsByAgeFormattedData);

      const studentsByCountryFormattedData = data.studentsByCountry.map(
        (student) => {
          return {
            count: student.count,
            country: student.country || 'Não informado',
          };
        },
      );
      setStudentsByCountry(studentsByCountryFormattedData);

      const studentsByCityFormattedData = data.studentsByCity.map((student) => {
        return {
          count: student.count,
          city: student.address_city || 'Não informado',
        };
      });
      setStudentsByCity(studentsByCityFormattedData);

      const annualBillingFormattedData = months.map((month, index) => {
        return {
          name: month,
          count:
            data.annualBilling.find(
              (annualData) => parseInt(annualData.month, 10) === index,
            )?.sum || 0,
        };
      });
      setAnnualBilling(annualBillingFormattedData);
    } catch (error) {
      console.log('error:', error);
      toast.error('Houve um erro ao buscar os dados dos gráficos');
    }
  }, [startDate, endDate, professional]);

  const packagesChartsMemo = useMemo(() => {
    const packagesData = comertialReportsdata?.packages?.map(
      (billingPackage) => {
        const monthsData = months.map((month, index) => {
          const data = comertialReportsdata?.annualBillingByPackages.find(
            (billing) =>
              billing.name === billingPackage &&
              parseInt(billing.month, 10) === index,
          );

          const formattedData = {
            month,
            sum: data?.sum || 0,
            name: data?.name || billingPackage,
            count: data?.count || 0,
          };

          return formattedData;
        });

        return monthsData;
      },
    );

    return packagesData?.map((packageData, index) => {
      return (
        <S.ChartContainer key={`package-${index}`}>
          <S.ChartTitle>Total de no pacote {packageData[0].name}</S.ChartTitle>
          <BarChart width={730} height={250} data={packageData} maxBarSize={25}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip
              labelStyle={{ color: '#000000' }}
              itemStyle={{ color: '#000000' }}
            />
            <Legend />
            <Bar dataKey="sum" name="Incrições" fill="#FFDF22" />
          </BarChart>
        </S.ChartContainer>
      );
    });
  }, [comertialReportsdata]);

  useEffect(() => {
    getComercialReports();
  }, [getComercialReports]);

  return (
    <Layout title="I.BOSS">
      <S.Header>
        <S.HeaderWrapper onClick={() => goBack()}>
          <IoIosArrowBack color="#FFDF22" size={24} />
          <S.HeaderLabel>Comercial</S.HeaderLabel>
        </S.HeaderWrapper>

        <ExplainDate initial={startDate || ''} last={endDate || ''} />
      </S.Header>

      <S.Container>
        <S.ChartContainer>
          <S.ChartTitle>Total de alunos no ano</S.ChartTitle>
          <BarChart
            width={730}
            height={250}
            data={trainingCount}
            maxBarSize={25}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="status" />
            <YAxis />
            <Tooltip
              labelStyle={{ color: '#000000' }}
              itemStyle={{ color: '#000000' }}
            />
            <Legend />
            <Bar dataKey="count" name="Incrições" fill="#FFDF22" />
          </BarChart>
        </S.ChartContainer>

        <S.ChartContainer>
          <S.ChartTitle>Total de alunos por idade</S.ChartTitle>
          <BarChart
            width={730}
            height={250}
            data={studentsByAge}
            maxBarSize={25}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="status" />
            <YAxis />
            <Tooltip
              labelStyle={{ color: '#000000' }}
              itemStyle={{ color: '#000000' }}
            />
            <Legend />
            <Bar dataKey="count" name="Incrições" fill="#FFDF22" />
          </BarChart>
        </S.ChartContainer>

        <S.ChartContainer>
          <S.ChartTitle>Total de alunos por país</S.ChartTitle>
          <BarChart
            width={730}
            height={250}
            data={studentsByCountry}
            maxBarSize={25}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="country" />
            <YAxis />
            <Tooltip
              labelStyle={{ color: '#000000' }}
              itemStyle={{ color: '#000000' }}
            />
            <Legend />
            <Bar dataKey="count" name="Incrições" fill="#FFDF22" />
          </BarChart>
        </S.ChartContainer>

        <S.ChartContainer>
          <S.ChartTitle>Total de alunos por cidade</S.ChartTitle>
          <BarChart
            width={730}
            height={250}
            data={studentsByCity}
            maxBarSize={25}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="city" />
            <YAxis />
            <Tooltip
              labelStyle={{ color: '#000000' }}
              itemStyle={{ color: '#000000' }}
            />
            <Legend />
            <Bar dataKey="count" name="Incrições" fill="#FFDF22" />
          </BarChart>
        </S.ChartContainer>

        <S.ChartContainer>
          <S.ChartTitle>Total de ganhos</S.ChartTitle>
          <AreaChart
            width={730}
            height={250}
            data={annualBilling}
            maxBarSize={25}
          >
            <defs>
              <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FFDF22" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#FFDF22" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="name" />
            <YAxis />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip
              labelStyle={{ color: '#000000' }}
              itemStyle={{ color: '#000000' }}
            />
            <Area
              type="monotone"
              dataKey="count"
              name="Valor"
              stroke="#FFDF22"
              fillOpacity={1}
              fill="url(#colorUv)"
            />
          </AreaChart>
        </S.ChartContainer>

        {packagesChartsMemo}
      </S.Container>
    </Layout>
  );
};

export default CommercialReports;
